"use client";

import React from "react";
import { ArrowRight, BookOpen, CircleCheck as CheckCircle2, Clock, Download, FileCheck, Headphones, ShieldCheck, Wrench } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Link } from "@/components/common/Link";
const supportPillars = [{
  icon: Headphones,
  title: "Direct Specialist Helpdesk",
  description: "Rapid-response technical assistance from analytical instrumentation engineers across Ireland and the UK.",
  tag: "Priority Response",
  points: ["Direct telephone & remote diagnostic triage", "Escalation pathways to senior application chemists", "Standardised ticketing with full audit trails"]
}, {
  icon: Wrench,
  title: "Planned Preventive Maintenance",
  description: "Scheduled servicing and component recalibrations to ensure maximum instrument uptime and measurement accuracy.",
  tag: "Uptime Assurance",
  points: ["OEM-specified replacement consumable kits", "Comprehensive multi-point system checks", "Custom service intervals aligned to run rates"]
}, {
  icon: ShieldCheck,
  title: "Qualification & Compliance (IQ/OQ)",
  description: "Rigorous protocol execution and regulatory compliance documentation tailored for GMP/GLP environments.",
  tag: "Regulated Environments",
  points: ["Fully certified traceable test equipment", "Turnkey re-qualification packages after repair", "Audit-ready data logs and sign-off records"]
}, {
  icon: BookOpen,
  title: "Application Training & Method Support",
  description: "Practical operator instruction and method optimization to maximize laboratory throughput and analyst confidence.",
  tag: "Skill Transfer",
  points: ["Hands-on operator workshops on-site or online", "Troubleshooting run failures and sample prep", "Continuous methodology updates and protocols"]
}];
const resourceDownloads = [{
  title: "Instrument Qualification Protocol Checklist",
  category: "Compliance Guide",
  format: "PDF Document",
  size: "1.8 MB",
  path: "/ContactPage"
}, {
  title: "Preventive Maintenance Schedule Template",
  category: "Operational Resource",
  format: "PDF Document",
  size: "2.4 MB",
  path: "/ServicesPage"
}, {
  title: "Regulated Laboratory Best Practice Whitepaper",
  category: "Technical Report",
  format: "PDF Document",
  size: "3.1 MB",
  path: "/SolutionsPage"
}];
export default function Support() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.12,
        delayChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0,
      y: shouldReduceMotion ? 0 : 18
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.45,
        ease: "easeOut"
      }
    }
  };
  return <section id="continuoussupportandresources" data-nav="light" className="bg-background text-foreground py-16 md:py-24 transition-colors">
      <div className="page-container">
        {/* Header Block */}
        <div className="max-w-3xl mx-auto text-center mb-14 md:mb-18">
          <div className="flex items-center justify-center mb-4">
            <Badge variant="outline" className="px-3 py-1 font-medium text-xs tracking-wider uppercase border-border bg-muted/40">
              Lifecycle Support & Knowledge
            </Badge>
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-default font-bold tracking-tight text-foreground mb-5">
            Continuous Technical Support and Practical Resources
          </h2>
          <p className="text-base md:text-lg text-muted-foreground leading-relaxed font-sans">
            Ensuring reliable performance, rigorous compliance, and sustained productivity for analytical laboratories across Ireland and the UK throughout the life of your systems.
          </p>
        </div>

        {/* 4-Pillar Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: "-50px"
      }} className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8 mb-16">
          {supportPillars.map((pillar, i) => {
          const Icon = pillar.icon;
          return <motion.div key={pillar.title} variants={itemVariants} data-index={i}>
                <Card className="h-full border border-border shadow-xs hover:shadow-md transition-shadow duration-200 bg-card">
                  <CardHeader className="pb-4">
                    <div className="flex items-center justify-between gap-4 mb-3">
                      <div className="w-12 h-12 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0">
                        <Icon className="size-6" />
                      </div>
                      <Badge variant="secondary" className="font-normal text-xs">
                        {pillar.tag}
                      </Badge>
                    </div>
                    <CardTitle className="text-xl font-bold font-default text-card-foreground">
                      {pillar.title}
                    </CardTitle>
                    <CardDescription className="text-sm text-muted-foreground leading-normal mt-1">
                      {pillar.description}
                    </CardDescription>
                  </CardHeader>

                  <CardContent className="pt-0">
                    <Separator className="mb-4" />
                    <ul className="space-y-2.5">
                      {pillar.points.map((point, pointIndex) => <li key={pointIndex} data-index={pointIndex} className="flex items-start gap-2.5 text-sm text-muted-foreground">
                          <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                          <span className="font-sans">{point}</span>
                        </li>)}
                    </ul>
                  </CardContent>
                </Card>
              </motion.div>;
        })}
        </motion.div>

        {/* Resource Downloads & Knowledge Base Strip */}
        <div className="rounded-2xl border border-border bg-secondary/50 p-6 md:p-8 lg:p-10 shadow-xs">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            {/* Left Context Box */}
            <div className="lg:col-span-5 space-y-4">
              <div className="flex items-center gap-2 text-primary font-semibold text-sm">
                <FileCheck className="size-4" />
                <span>Technical Documentation & Protocols</span>
              </div>
              <h3 className="text-2xl md:text-3xl font-bold font-default text-foreground">
                Audit-Ready Resource Hub
              </h3>
              <p className="text-sm text-muted-foreground leading-relaxed font-sans">
                Access technical literature, maintenance blueprints, and compliance protocols compiled by our instrumentation engineers to support your internal SOPs and quality audits.
              </p>
              <div className="pt-2 flex flex-wrap gap-3">
                <Button asChild variant="default" size="default">
                  <Link to="/ContactPage" className="inline-flex items-center gap-2">
                    Request Support Service
                    <ArrowRight className="size-4" />
                  </Link>
                </Button>
                <Button asChild variant="outline" size="default">
                  <Link to="/ServicesPage">
                    Explore Service Plans
                  </Link>
                </Button>
              </div>
            </div>

            {/* Right Resource Links */}
            <div className="lg:col-span-7 grid grid-cols-1 gap-3.5">
              {resourceDownloads.map((resource, i) => <div key={resource.title} data-index={i} className="flex flex-col sm:flex-row sm:items-center justify-between p-4 rounded-lg bg-card border border-border hover:border-primary/40 shadow-2xs transition-colors gap-3">
                  <div className="flex items-start gap-3.5 min-w-0">
                    <div className="p-2.5 rounded-md bg-muted text-foreground shrink-0 mt-0.5 sm:mt-0">
                      <Download className="size-4" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs font-medium text-primary uppercase tracking-wide">
                        {resource.category}
                      </p>
                      <h4 className="text-sm font-semibold text-foreground truncate font-default">
                        {resource.title}
                      </h4>
                      <p className="text-xs text-muted-foreground">
                        {resource.format} • {resource.size}
                      </p>
                    </div>
                  </div>

                  <div className="shrink-0 self-end sm:self-center">
                    <Button asChild variant="ghost" size="sm" className="text-xs">
                      <Link to={resource.path} className="flex items-center gap-1.5">
                        <span>Access</span>
                        <ArrowRight className="size-3.5" />
                      </Link>
                    </Button>
                  </div>
                </div>)}
            </div>
          </div>
        </div>

        {/* Emergency / Service SLA Banner */}
        <div className="mt-8 flex flex-col sm:flex-row items-center justify-between gap-4 p-4 md:p-5 rounded-xl bg-muted/60 border border-border text-xs md:text-sm text-muted-foreground">
          <div className="flex items-center gap-3">
            <Clock className="size-5 text-primary shrink-0" />
            <span className="font-sans">
              <strong>Need urgent breakdown support?</strong> Our field engineers provide rapid on-site assistance across Dublin, Cork, Belfast, and UK regions.
            </span>
          </div>
          <Button asChild variant="link" size="sm" className="font-semibold shrink-0 p-0 text-primary">
            <Link to="/ContactPage" className="inline-flex items-center gap-1">
              Contact our engineering team
              <ArrowRight className="size-3.5" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}